import {
  Box,
  Field as ChakraField,
  HStack,
  RadioGroup,
  Stack,
  Text,
  VStack
} from "@chakra-ui/react"
import { getStepVerbIcon } from "@pathwright/features/src/modules/path/utils"
import { usePathwrightContext } from "@pathwright/features/src/modules/pathwright/PathwrightContext"
import SpaceIdentity from "@pathwright/features/src/modules/space/identity/SpaceIdentity"
import {
  NoteFragment,
  NoteVisibility,
  PathItemType
} from "@pathwright/graphql-client/src/generated"
import LazyRedactorEditor from "@pathwright/ui/src/components/richtext/LazyRedactorEditor"
import { useLocalStorage } from "@pathwright/utils"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import DiscardButton from "@pathwright/ux/src/components/form/DiscardButton"
import Field from "@pathwright/ux/src/components/form/Field"
import SubmitButton from "@pathwright/ux/src/components/form/SubmitButton"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { Select } from "chakra-react-select"
import { Form, FormikProps } from "formik"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import { PathItem } from "../types"
import { NoteInputContext, useNoteContext } from "./NoteContext"

const noteSchema = z.object({
  title: z
    .string()
    .min(1, "Required")
    .max(255, "Must be less than 255 characters"),
  body: z.string().min(1, "Required"),
  target: z.number(),
  visibility: z.nativeEnum(NoteVisibility)
})

const validate = toFormikValidate(noteSchema)

type NoteValues = z.infer<typeof noteSchema>

// Type guard for notable path items (lessons and resources).
export function isNotablePathItem(
  item: PathItem
): item is PathItem & { type: Omit<PathItemType, PathItemType.Divider> } {
  return item.type !== PathItemType.Divider
}

// Scopes for state storage to a note or context.
// Deriving the context from the note or passed context.
export function getNoteFormStateKey(
  identifier: NoteFragment | NoteInputContext
): ["note", number, NoteInputContext] | ["note", NoteInputContext] {
  if ("id" in identifier) {
    // Working with note.
    return [
      "note",
      identifier?.id,
      {
        resource_id: identifier.resource_id,
        cohort_id: identifier.cohort_id,
        path_item_source_id: identifier.step_id
      }
    ]
  } else {
    // Working with context.
    return ["note", identifier]
  }
}

function NoteForm() {
  const { t, tc } = useTranslate()
  const { school, me } = usePathwrightContext()
  const {
    context,
    note,
    setNoteSelection,
    confirmSelection,
    notePermissions,
    flattenedPathItems,
    pathItem
  } = useNoteContext()

  const bgColor = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.600")

  const actionLabel = note
    ? t("notes.form.update_note")
    : t("notes.form.save_note")

  const canSelectVisibility =
    notePermissions.visibilityOptions.has(NoteVisibility.Staff) ||
    notePermissions.visibilityOptions.has(NoteVisibility.Everyone)

  // Store last used visibility in local storage for convenience.
  const [lastUsedVisibility, setLastUsedVisibility] =
    useLocalStorage<NoteVisibility>("lastUsedNoteVisibility")

  // Default to last used visibility or "author" if none set.
  // Ensure it's a valid option for the current note as use may have different
  // permissions in different contexts.
  let defaultVisibility = lastUsedVisibility || NoteVisibility.Author
  if (!notePermissions.visibilityOptions.has(defaultVisibility)) {
    defaultVisibility = NoteVisibility.Author
  }

  const notablePathItems = flattenedPathItems?.filter(isNotablePathItem)

  // We don't permit changing target for existing notes if the user lacks permissions
  const canSetTarget = !note && !!notablePathItems?.length

  const target = pathItem || notablePathItems?.[0]
  const targetId = target?.source_id

  // Scoping to current note || context.
  const formStateKey = getNoteFormStateKey(
    note || {
      ...context,
      path_item_source_id: targetId
    }
  )

  return (
    <Box
      className="note-form-view"
      bg={bgColor}
      p={6}
      w="full"
      borderRadius="md"
      border={`1px solid ${borderColor}`}
      css={{
        "& .redactor-box": {
          mb: 0
        }
      }}
    >
      <FormikWithDiscardFormState
        formStateKey={formStateKey}
        initialValues={{
          title: note?.title || "",
          body: note?.body || "",
          target: note?.step_id || targetId,
          visibility: note?.visibility || defaultVisibility
        }}
        onSubmit={({ target, ...noteInput }) => {
          // NOTE: we pull out the target as that's part of the NoteContextInput
          // which is supplied via the note or the noteSelection. This is mainly
          // to allow confirmSelection to support a single note arg signature for
          // both create and update.
          return confirmSelection(noteInput)
        }}
        validate={validate}
        formStateOnSubmit="clearOptimistic"
      >
        {(form: FormikProps<NoteValues>) => (
          <Form>
            <KeyboardFormSubmit />
            <VStack gap={6} align="stretch">
              {/* Form container */}
              <VStack
                gap={4}
                className="note-form-container note-form-input note-body-text"
              >
                {/* Note title */}
                <Field
                  name="title"
                  label={t("notes.form.note_title")}
                  placeholder={t("notes.form.title_placeholder")}
                  className="note-title-input"
                  variant="outline"
                  autoFocus
                />

                {/* Note body */}
                <Field
                  name="body"
                  label={t("notes.form.view.your_note")}
                  as={LazyRedactorEditor}
                  // Override value passed by Formik to avoid slower updates due to controlled component.
                  // Likely need to revisit Form State Storage for efficiency (happens with the Input field as well).
                  value={undefined}
                  initialValue={form.values.body}
                  onChange={(body: string) => form.setFieldValue("body", body)}
                  onBlur={() => form.setFieldTouched("body", true)}
                  placeholder={t("notes.form.view.placeholder", {
                    placeholder: target?.name
                  })}
                  className="note-body-input"
                  minH="120px"
                  bg="white"
                />

                {/* Filter menu for attachment target */}
                {canSetTarget && (
                  <Field
                    name="target"
                    label={t("notes.form.attach_to")}
                    as={Select<PathItem>}
                    options={notablePathItems}
                    value={notablePathItems?.find(
                      (item) => item.source_id === form.values.target
                    )}
                    // Required for telling react-select how to determine which option is selected.
                    getOptionValue={(option) => option.id + ""}
                    getOptionLabel={(option) => option.name || ""}
                    onChange={(option) => {
                      if (!option) return
                      form.setFieldValue("target", option.id)
                      // Can only update target on "create" so we don't need to handle the "update" case.
                      setNoteSelection({
                        action: "create",
                        itemId: option.source_id
                      })
                    }}
                    formatOptionLabel={(option) => (
                      <HStack gap={3}>
                        {!!option.verb && (
                          <Pathicon
                            icon={getStepVerbIcon(option.verb.toLowerCase())}
                          />
                        )}
                        {option.name && (
                          <Text as="span" fontSize="sm" color="inherit">
                            {option.name}
                          </Text>
                        )}
                      </HStack>
                    )}
                    variant="outline"
                  />
                )}

                {/* Visibility options */}
                {canSelectVisibility && (
                  <ChakraField.Root>
                    <ChakraField.Label>
                      {t("notes.form.visible_to")}
                    </ChakraField.Label>
                    <RadioGroup.Root
                      value={form.values.visibility + ""}
                      onValueChange={(e) => {
                        form.setFieldValue("visibility", e.value)
                        setLastUsedVisibility(e.value as NoteVisibility)
                      }}
                      name="visibility"
                      className="note-visibility-options"
                      size="sm"
                    >
                      <Stack gap={1}>
                        {/* Author only */}
                        <Box
                          w="full"
                          borderRadius="card"
                          _hover={{
                            bg: "blackAlpha.50"
                          }}
                        >
                          <RadioGroup.Item
                            w="full"
                            p={2}
                            value={NoteVisibility.Author}
                            className="option-user"
                            // Doesn't currently make sense to change visibility of a note you didn't author
                            // to be author-only?
                            disabled={!!note && note.user.id !== me.id}
                          >
                            <RadioGroup.ItemHiddenInput />
                            <RadioGroup.ItemIndicator />
                            <RadioGroup.ItemText>
                              <HStack gap={3} align="center">
                                <UserAvatar size="sm" user={me} />
                                <Text m={0}>
                                  <Text as="strong" fontWeight="bold">
                                    {t("notes.form.you")}
                                  </Text>{" "}
                                  {t("notes.form.only_you")}
                                </Text>
                              </HStack>
                            </RadioGroup.ItemText>
                          </RadioGroup.Item>
                        </Box>

                        {/* Staff (if permitted) */}
                        {notePermissions.visibilityOptions.has(
                          NoteVisibility.Staff
                        ) && (
                          <Box
                            w="full"
                            borderRadius="card"
                            _hover={{
                              bg: "blackAlpha.50"
                            }}
                          >
                            <RadioGroup.Item
                              w="full"
                              p={2}
                              value={NoteVisibility.Staff}
                              className="option-discussion"
                            >
                              <RadioGroup.ItemHiddenInput />
                              <RadioGroup.ItemIndicator />
                              <RadioGroup.ItemText>
                                <HStack gap={3} align="center">
                                  <SpaceIdentity space={school} />
                                  <Text m={0}>
                                    <Text as="strong" fontWeight="bold">
                                      {t("notes.form.all_staff")}
                                    </Text>{" "}
                                    {t("notes.form.can_see")}
                                  </Text>
                                </HStack>
                              </RadioGroup.ItemText>
                            </RadioGroup.Item>
                          </Box>
                        )}

                        {/* Everyone */}
                        {notePermissions.visibilityOptions.has(
                          NoteVisibility.Everyone
                        ) && (
                          <Box
                            w="full"
                            borderRadius="card"
                            _hover={{
                              bg: "blackAlpha.50"
                            }}
                          >
                            <RadioGroup.Item
                              w="full"
                              p={2}
                              value={NoteVisibility.Everyone}
                              className="option-user"
                            >
                              <RadioGroup.ItemHiddenInput />
                              <RadioGroup.ItemIndicator />
                              <RadioGroup.ItemText>
                                <HStack gap={3} align="center">
                                  <Box
                                    className="pw-framed-icon circle note-option-icon"
                                    w="2rem"
                                    h="2rem"
                                    minW="2rem"
                                    minH="2rem"
                                    borderRadius="full"
                                    bg="brand.100"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Pathicon
                                      icon="group-2"
                                      color="brand.600"
                                    />
                                  </Box>
                                  <Text m={0}>
                                    <Text as="strong" fontWeight="bold">
                                      {t("notes.form.everyone")}
                                    </Text>{" "}
                                    {t("notes.form.all_users")}
                                  </Text>
                                </HStack>
                              </RadioGroup.ItemText>
                            </RadioGroup.Item>
                          </Box>
                        )}
                      </Stack>
                    </RadioGroup.Root>
                  </ChakraField.Root>
                )}
              </VStack>

              {/* Action buttons */}
              <VStack gap={4} className="note-form-action-buttons">
                {!canSelectVisibility && (
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    {t("notes.form.private_note")}
                  </Text>
                )}

                <HStack gap={2}>
                  <SubmitButton
                    loadingText={tc("notes.form.saving")}
                    className="save-note"
                  >
                    {actionLabel}
                  </SubmitButton>

                  <Text as="span" color="gray.500">
                    {t("notes.form.or")}
                  </Text>

                  <DiscardButton
                    colorPalette="brand"
                    variant="plain"
                    disabled={false}
                    onClick={() => setNoteSelection({ action: "none" })}
                    className="cancel-note"
                    p={0}
                    w="min-content"
                    display="inline-block"
                    bg="none"
                  >
                    {form.dirty
                      ? tc("notes.form.discard")
                      : tc("notes.form.cancel")}
                  </DiscardButton>
                </HStack>
              </VStack>
            </VStack>
          </Form>
        )}
      </FormikWithDiscardFormState>
    </Box>
  )
}

export default NoteForm
